import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getAccounts } from '../api/accountsApi';
import { Account } from '../models/account';

interface AccountsContextType {
    accounts: Account[];
    isLoading: boolean;
    error: string | null;
    addAccount: (account: Account) => void;
    removeAccount: (id: string) => void;
    refreshAccounts: () => Promise<void>;
}

const AccountsContext = createContext<AccountsContextType | undefined>(undefined);

export const AccountsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadAccounts = async () => {
        if (!isAuthenticated) {
            setAccounts([]);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const fetchedAccounts = await getAccounts();
            setAccounts(fetchedAccounts.data);
        } catch (err) {
            console.error('Error fetching accounts:', err);
            setError('Failed to load accounts');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Only load accounts when auth is done loading and user is authenticated
        if (!isAuthLoading && isAuthenticated) {
            loadAccounts();
        }
    }, [isAuthLoading, isAuthenticated]);

    const addAccount = (account: Account) => {
        setAccounts((prevAccounts) => [...prevAccounts, account]);
    };

    const removeAccount = (id: string) => {
        setAccounts((prevAccounts) => prevAccounts.filter((account) => account.id !== id));
    };

    const refreshAccounts = async () => {
        await loadAccounts();
    };

    return (
        <AccountsContext.Provider 
            value={{ 
                accounts, 
                isLoading: isAuthLoading || isLoading,
                error,
                addAccount, 
                removeAccount,
                refreshAccounts
            }}
        >
            {children}
        </AccountsContext.Provider>
    );
};

export const useAccounts = () => {
    const context = useContext(AccountsContext);
    if (context === undefined) {
        throw new Error('useAccounts must be used within an AccountsProvider');
    }
    return context;
};